import { z } from 'zod';
import { IsoTime, Severity } from './basics.js';
/*
 * Vulnerabilities in what is installed (build plan P11.8.8), without Wazuh's
 * indexer: the central API keeps OSV's open advisory data for the releases the
 * fleet runs (Ubuntu's from Canonical, Debian's from Debian's security tracker,
 * both as OSV publishes them), and matches each server's inventory (P11.8.7)
 * against it. Nothing about the fleet leaves central: the data is downloaded,
 * never queried with a package list.
 */
/** `Ubuntu:24.04:LTS`, `Debian:12`: OSV's names for a release. */
export const OsvEcosystem = z
    .string()
    .regex(/^(Ubuntu:\d{2}\.\d{2}(:LTS)?|Debian:\d{1,2})$/, 'an Ubuntu or Debian release');
/**
 * The OSV ecosystem for a server's system as syscollector names it
 * (`ubuntu`, `24.04.5 LTS (Noble Numbat)`; `debian`, `12 (bookworm)`), or null
 * where Bartizo has no advisory data for it.
 */
export function osvEcosystemFor(platform, version) {
    const text = (version ?? '').trim();
    if (platform === 'ubuntu') {
        const release = /^(\d{2}\.\d{2})/.exec(text)?.[1];
        if (release === undefined)
            return null;
        return /\bLTS\b/.test(text) ? `Ubuntu:${release}:LTS` : `Ubuntu:${release}`;
    }
    if (platform === 'debian') {
        const major = /^(\d{1,2})\b/.exec(text)?.[1];
        return major === undefined ? null : `Debian:${major}`;
    }
    return null;
}
/** `UBUNTU-CVE-2024-6387`, `DEBIAN-CVE-2024-6387`, `DSA-5724-1`… as OSV names them. */
export const AdvisoryId = z
    .string()
    .min(3)
    .max(80)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/);
export const VULNERABILITY_LIMITS = {
    summary: 600,
    findings: 5_000,
    aliases: 10,
};
/** One vulnerable package on one server. */
export const VulnerabilityFinding = z.object({
    advisoryId: AdvisoryId,
    /** The CVEs it is about, first the one it is named after. */
    cves: z.array(z.string().max(40)).max(VULNERABILITY_LIMITS.aliases),
    summary: z.string().max(VULNERABILITY_LIMITS.summary).nullable(),
    severity: Severity,
    /** CVSS 3.x base score, where the advisory carries a vector. */
    cvssScore: z.number().min(0).max(10).nullable(),
    /** The installed (binary) package. */
    package: z.string().min(1).max(255),
    /** The source package the advisory names, where it differs. */
    source: z.string().max(255).nullable(),
    installedVersion: z.string().max(255),
    /** The first version without it; null: no fix released yet. */
    fixedVersion: z.string().max(255).nullable(),
    /** Ubuntu: the fix is only in Ubuntu Pro (ESM). */
    needsPro: z.boolean(),
    publishedAt: IsoTime.nullable(),
    firstSeenAt: IsoTime,
});
/** `GET /v1/servers/:id/vulnerabilities`. */
export const ServerVulnerabilities = z.object({
    /** The release matched against; null where Bartizo has no data for this system. */
    ecosystem: OsvEcosystem.nullable(),
    /** Fixable first, then by severity, newest advisory first. */
    findings: z.array(VulnerabilityFinding).max(VULNERABILITY_LIMITS.findings),
    /** How many there were, where more than the list carries. */
    total: z.number().int().nonnegative(),
    /** `apt-get install --only-upgrade …` for every package with a fix, to copy; never run by Bartizo. */
    upgradeCommand: z.string().max(20_000).nullable(),
    /** When the advisory data for this release was last brought up to date. */
    advisoriesAt: IsoTime.nullable(),
    /** When this server was last matched against it. */
    matchedAt: IsoTime.nullable(),
});
/**
 * Ubuntu's priority (`negligible`…`critical`) or Debian's urgency, as Bartizo's
 * severity; undefined for what neither says (`not yet assigned`).
 */
export function distroSeverity(value) {
    switch (value?.trim().toLowerCase().replace(/\*+$/, '')) {
        case 'critical':
            return 'critical';
        case 'high':
            return 'high';
        case 'medium':
            return 'medium';
        case 'low':
            return 'low';
        case 'negligible':
        case 'unimportant':
            return 'info';
        default:
            return undefined;
    }
}
//# sourceMappingURL=vulnerabilities.js.map